/**
 * #440, #700 — keep the branding-removal fee paired with its design.
 *
 * The fee line renders without a quantity stepper or a remove button, but the
 * DESIGN line it pays for keeps both. So the customer can change the design in
 * ways the fee cannot follow on its own. Two things drift, and this file owns
 * both so there is a single owner of the pairing:
 *
 * 1. **Orphans** (#440/#554) — a line carrying `_branding_removal_for` whose
 *    target batch no longer has a design line in the cart is removed. Also the
 *    propertyless ghost (#554): the branding-removal product with no pointer,
 *    which the property-based check alone cannot see.
 * 2. **Quantity** (#700) — removal is charged per piece, so the fee's quantity
 *    must equal its design line's quantity. When the customer changes the design
 *    quantity in the cart, nothing in Shopify moves the fee with it; this does.
 *
 * ## What this is NOT
 *
 * Not enforcement. `/cart/change.js` is reachable from devtools, so a customer
 * can delete the fee, or set its quantity to 1 against a 100-unit design, and
 * keep the flagged design line. **This runs in the customer's own browser and
 * can never be the guarantee.**
 *
 * The real gate is the `orders/paid` webhook, which grants the entitlement only
 * against a real, positively-priced Shopify line **whose quantity equals the
 * design's** (`fee_quantity_mismatch`, #700) — a rule the browser cannot reach.
 * The Cart & Checkout Validation function would catch it before payment instead
 * of after, but it needs Shopify Plus and both stores are on Grow, so it does
 * not run (#440 Phase 6, parked). This file exists so the ordinary case behaves
 * sensibly, not so the money is safe.
 */
(function () {
  'use strict';

  var FEE_PROPERTY = '_branding_removal_for';
  var FEE_HANDLE = 'branding-removal';
  var sweeping = false;

  function propertiesOf(item) {
    return (item && item.properties) || {};
  }

  function isBrandingFeeItem(item) {
    if (!item) return false;
    if (propertiesOf(item)[FEE_PROPERTY]) return true;
    // Propertyless ghost: identify by product handle from /cart.js.
    return item.handle === FEE_HANDLE;
  }

  /**
   * How many units each batch has in the cart, from its DESIGN line.
   *
   * Summed, not overwritten, to match how the webhook computes the quantity a
   * fee has to cover: if two lines somehow name the same batch, the fee owes
   * their total. Fee lines are excluded so a fee never counts towards the
   * quantity it is supposed to be mirroring.
   */
  function designQuantitiesByBatch(items) {
    var quantities = {};
    items.forEach(function (item) {
      if (isBrandingFeeItem(item)) return;
      var batchId = propertiesOf(item)['_batch_id'];
      if (!batchId) return;
      quantities[batchId] = (quantities[batchId] || 0) + item.quantity;
    });
    return quantities;
  }

  /** Line keys of fee lines whose design line is gone (or never existed). */
  function findOrphanFeeKeys(items) {
    var liveBatchIds = designQuantitiesByBatch(items);

    return items
      .filter(function (item) {
        if (!isBrandingFeeItem(item)) return false;
        var target = propertiesOf(item)[FEE_PROPERTY];
        // Ghost (no pointer) or unpaired pointer → remove.
        return !target || !liveBatchIds[target];
      })
      .map(function (item) {
        return item.key;
      });
  }

  /**
   * Fee lines whose quantity has drifted from their design's (#700).
   *
   * Returns `{ key, quantity }` pairs to POST. Orphans are excluded by the
   * `liveBatchIds[target]` test — a fee whose design is gone must be REMOVED,
   * not resized to a quantity that no longer exists.
   */
  function findFeeQuantityFixes(items) {
    var designQuantities = designQuantitiesByBatch(items);

    return items
      .filter(function (item) {
        if (!isBrandingFeeItem(item)) return false;
        var target = propertiesOf(item)[FEE_PROPERTY];
        if (!target || !designQuantities[target]) return false;
        return item.quantity !== designQuantities[target];
      })
      .map(function (item) {
        return { key: item.key, quantity: designQuantities[propertiesOf(item)[FEE_PROPERTY]] };
      });
  }

  async function sweep() {
    if (sweeping) return;
    sweeping = true;
    try {
      var cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) return;
      var cart = await cartResponse.json();
      var items = cart.items || [];
      var orphans = findOrphanFeeKeys(items);
      // Computed BEFORE the orphan removals, then filtered against them below:
      // both lists are derived from the same cart snapshot, so a key cannot
      // appear in both, but reading the snapshot once keeps the two views
      // consistent.
      var quantityFixes = findFeeQuantityFixes(items).filter(function (fix) {
        return orphans.indexOf(fix.key) === -1;
      });
      if (orphans.length === 0 && quantityFixes.length === 0) return;

      // One at a time: /cart/change.js takes a single line, and each change
      // re-keys nothing but does return the updated cart.
      //
      // Orphans first. A fee whose design was deleted must go away, not be
      // resized — and removing it first means the quantity pass never has to
      // reason about lines that are about to disappear.
      for (var i = 0; i < orphans.length; i++) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: orphans[i], quantity: 0 })
        });
      }

      // #700: then pull each surviving fee back onto its design's quantity.
      for (var j = 0; j < quantityFixes.length; j++) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: quantityFixes[j].key, quantity: quantityFixes[j].quantity })
        });
      }

      // Do NOT reload. A reload re-reads the SSR live_cart metafield, which
      // still holds the orphan until sync completes — that was the 2–5s
      // appear/disappear loop (#554). Sync the cleaned cart, then refresh
      // cart sections in place.
      document.dispatchEvent(new CustomEvent('nfckey:cart-mutated'));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));
    } catch (err) {
      // Never break the cart page over this — the webhook is the real gate.
      console.error('[BrandingFeePairing] sweep failed:', err);
    } finally {
      sweeping = false;
    }
  }

  document.addEventListener('DOMContentLoaded', sweep);
  // `cart:update`, NOT `cart:updated` — the name is defined once by the theme
  // itself, as `ThemeEvents.cartUpdate` in `assets/events.js`, and dispatched
  // by `component-cart-items.js` after every AJAX line change. The guessed
  // name meant removing a design line left its fee behind until the customer
  // happened to reload the page, which is exactly the orphan this file exists
  // to prevent (#553). CartUpdateEvent bubbles, so listening on document
  // catches it from the cart page and the cart drawer alike.
  document.addEventListener('cart:update', sweep);
})();
